/**
 * Everything the preview shows about one bank question that the pool grid does
 * not: the answer as students see it, how the cohort actually performed, and
 * every version the item has been through.
 *
 * Derived rather than authored. A hand-written preview payload for 1,200 rows
 * would be 1,200 rows of fiction to maintain, and the derivation has to be
 * deterministic anyway or reopening the same question would report a different
 * p-value than it did a second ago. Every number here comes from `hashUnit` on
 * the row's own id, so it is stable across reloads, snapshots, and tests.
 *
 * One rule holds the whole model together: **`pbi === "--"` means the item has
 * never been sat.** An unsat item has no p-value, no discrimination, no
 * distractor shares, and no cohort. That is a different statement from scoring
 * badly, and the preview says so rather than drawing a 0.
 */

import {
  discriminationIndex,
  hashUnit,
  type AssessmentBankQuestion,
} from "@/lib/mock/assessment-add-questions"
import { hotspotKeyForStem } from "@/lib/hotspot-stock"
import {
  authoredChoicesForStem,
  authoredFillBlankForStem,
  correctIndexForChoices,
} from "@/lib/question-answer-key"
import { TAGGED_DIFFICULTY_P_VALUE } from "@/lib/question-signals"
import {
  normalizeQuestionType,
  questionAnswerShape,
  questionTypeAllowsMultipleKeys,
  questionTypeChoiceCount,
  questionTypeLabel,
} from "@/lib/question-type"

/** One answer choice, with how the cohort split across it. */
export interface QuestionChoice {
  /** Letter students see: A, B, C… */
  letter: string
  text: string
  correct: boolean
  /** Share of examinees who picked it, 0..1. `null` when never sat. */
  share: number | null
}

/**
 * How the question is answered. Selection items show a key and distractor
 * analysis; structured auto-mark types show the accepted key; open items show
 * what a grader is told to look for.
 */
export type QuestionAnswerModel =
  | { kind: "choices"; choices: QuestionChoice[]; multiple: boolean }
  | {
      kind: "fill_blank"
      blanks: { id: string; label: string; accepted: string[] }[]
    }
  | {
      kind: "matching"
      pairs: { prompt: string; match: string }[]
    }
  | {
      kind: "hotspot"
      /** What the figure depicts, for the accessible name. */
      imageLabel: string
      /** Stimulus path. Coronary items use the anterior heart photograph. */
      imageSrc: string
      /** Correct zone on the stock figure, as % of the image box. */
      correctArea: {
        left: number
        top: number
        width: number
        height: number
      }
      regions: { id: string; label: string; correct: boolean }[]
    }
  | { kind: "open"; guidance: string }

export interface QuestionPerformance {
  /** p-value: share who answered correctly. `null` when never sat. */
  pValue: number | null
  pbi: number | null
  dIndex: number | null
  /**
   * Share correct in the top-scoring 27% of the cohort. `null` when never sat.
   * Derived with discrimination so Upper − Lower tracks D / PBI loosely.
   */
  upper27: number | null
  /** Share correct in the bottom-scoring 27% of the cohort. */
  lower27: number | null
  /** Examinees behind the stats. `null` when never sat. */
  cohort: number | null
}

export interface QuestionVersion {
  /** 1-based, oldest first in the number line, newest first in the list. */
  number: number
  /** What changed in this revision, in the author's words. */
  note: string
  /** Absent on surfaces that record the question but not who wrote it. */
  author?: string
  authorEmail?: string
  /** Set when an admin wrote this for someone else: the owner of record. */
  onBehalfOf?: string
  /** ISO date, formatted at the surface so tests can compare the raw value. */
  date: string
  /** The wording this version asks. */
  stem: string
  answer: QuestionAnswerModel
  performance: QuestionPerformance
  /** The version the bank serves today. Exactly one is current. */
  current: boolean
}

export interface QuestionPreview {
  /** Newest first, so the current version leads. */
  versions: QuestionVersion[]
  /** Cross-version facts: who owns it, where it lives, what it is worth. */
  createdOn: string
  updatedOn: string
}

/**
 * A revision written in the app, stacked on top of the derived history.
 *
 * The rest of this module invents an item's past from its id, which is right
 * for 1,200 rows nobody has touched and wrong the moment an author edits one:
 * a hash cannot know what they just wrote. So an edit is recorded on the row
 * and this module appends it as the newest version.
 *
 * It carries no performance numbers, and that is not a gap in the mock. A new
 * wording has not been sat by anyone; last cohort's p-value belongs to the
 * sentence they actually read, which is now the version below this one.
 */
export interface QuestionRevision {
  /** What the author changed, in their words. */
  note: string
  author: string
  authorEmail?: string
  /** Set when an admin wrote this for a faculty member who stays the owner. */
  onBehalfOf?: string
  /** ISO date. */
  date: string
  stem: string
  answer: QuestionAnswerModel
}

/**
 * Plausible answer text per topic, so a Cardiology stem does not offer
 * "Option B" as a distractor. Mock prose, not clinical guidance.
 */
const TOPIC_TERMS: Record<string, string[]> = {
  Cardiology: [
    "Sinoatrial node",
    "Atrioventricular node",
    "Bundle of His",
    "Purkinje fibres",
    "Left bundle branch",
  ],
  Neurology: [
    "Frontal lobe",
    "Parietal lobe",
    "Cerebellum",
    "Brainstem",
    "Hippocampus",
  ],
  Pathology: [
    "Coagulative necrosis",
    "Caseous necrosis",
    "Apoptosis",
    "Fatty change",
    "Fibrinoid necrosis",
  ],
  Pharmacology: [
    "Competitive antagonism",
    "Irreversible COX inhibition",
    "Partial agonism",
    "Enzyme induction",
    "First pass metabolism",
  ],
  "Cell Biology": [
    "Mitochondrial matrix",
    "Cytosol",
    "Rough endoplasmic reticulum",
    "Golgi apparatus",
    "Peroxisome",
  ],
  Clinical: [
    "Metabolic acidosis",
    "Respiratory alkalosis",
    "Metabolic alkalosis",
    "Respiratory acidosis",
    "Anion gap widening",
  ],
  Immunology: [
    "Alpha-1 receptors",
    "Beta-2 receptors",
    "Muscarinic receptors",
    "Histamine H1 receptors",
    "Beta-1 receptors",
  ],
  Microbiology: [
    "Gram positive cocci",
    "Gram negative rods",
    "Acid fast bacilli",
    "Spore forming anaerobes",
    "Atypical mycobacteria",
  ],
  Endocrinology: [
    "Anterior pituitary",
    "Adrenal cortex",
    "Thyroid follicular cells",
    "Pancreatic beta cells",
    "Parathyroid chief cells",
  ],
  Respiratory: [
    "Type II pneumocytes",
    "Bronchial smooth muscle",
    "Alveolar macrophages",
    "Goblet cells",
    "Pleural mesothelium",
  ],
  Renal: [
    "Proximal convoluted tubule",
    "Loop of Henle",
    "Distal convoluted tubule",
    "Collecting duct",
    "Glomerular capillaries",
  ],
  Musculoskeletal: [
    "Fall risk with unsteady gait",
    "Hip fracture precautions",
    "Pressure injury over a bony prominence",
    "Safe transfer with a gait belt",
    "Neurovascular check of the limb",
  ],
  General: [
    "Hand hygiene before contact",
    "Two patient identifiers",
    "Bed in the lowest position",
    "Call light within reach",
    "Side rails as ordered",
  ],
  Physiology: [
    "Sympathetic vasoconstriction",
    "Increased heart rate",
    "Reduced urine output",
    "Cool clammy skin",
    "Narrowed pulse pressure",
  ],
  "Diagnostic Reasoning": [
    "Serial cardiac enzymes",
    "12-lead ECG now",
    "Chest radiograph",
    "Repeat vital signs only",
    "Discharge without follow-up",
  ],
  "Emergency Recognition": [
    "Systolic blood pressure 78 mmHg after fluids",
    "Temperature 38.1 C",
    "Heart rate 102",
    "Respiratory rate 22",
    "Mild discomfort at rest",
  ],
  "Patient Communication": [
    "Benefit, risk, and when to call",
    "Medication name only",
    "Defer all questions to the next visit",
    "Written instructions the client can read",
    "Ask the client to teach the plan back",
  ],
  "Cardiovascular Conditions": [
    "Left anterior descending artery",
    "Right coronary artery",
    "Circumflex artery",
    "Sinoatrial node",
    "Aortic valve",
  ],
  "Pharmacology and Therapeutics": [
    "Hold the dose and notify the prescriber",
    "Give the usual adult dose",
    "Double the next dose",
    "Switch to a topical form",
    "Stop monitoring after one dose",
  ],
}

/**
 * Distractors for a selection item whose topic has no term list.
 *
 * Deliberately broken: "All of the above" beside "None of the above" over
 * three positional stems is the choice set Leo is meant to spot, and
 * `choiceSetIsBroken` reads exactly this shape. It is a **selection-item**
 * fallback and nothing else — see `FILL_BLANK_FALLBACK` below for why.
 */
const FALLBACK_TERMS = [
  "The first stated mechanism",
  "The second stated mechanism",
  "The third stated mechanism",
  "None of the above",
  "All of the above",
]

/**
 * Fillers for a fill-in item whose topic has no term list.
 *
 * Fill-in and matching used to draw from the same pool as the choice types,
 * which is how "Muscles of facial expression are innervated by cranial nerve
 * ____" came to accept **"None of the above"** as its answer. A blank is
 * typed into, so an answer to one has to be a thing a student could write —
 * "none of the above" is a position in a list that a fill-in item does not
 * have. These are short factual fillers, and `PLACEHOLDER_TERM` below is the
 * belt to this braces.
 */
const FILL_BLANK_FALLBACK = [
  "the primary structure named in the stem",
  "the first mechanism",
  "the named enzyme",
  "the stated value",
  "the named organ",
]

/**
 * A choice-list position, not an answer. Nothing typed into a blank and
 * nothing on either side of a matching pair may be one of these.
 */
const PLACEHOLDER_TERM = /\b(?:all|none) of the above\b|\bstated (?:mechanism|fact)\b|^the (?:first|second|third) stated\b/i

/** Terms usable where the student writes or pairs rather than picks. */
function nonPositionalTerms(pool: readonly string[], fallback: readonly string[]): string[] {
  const usable = pool.filter((term) => !PLACEHOLDER_TERM.test(term))
  return usable.length > 0 ? usable : [...fallback]
}

const LETTERS = ["A", "B", "C", "D", "E"]

/** What a grader is told to look for, by open type. */
const OPEN_GUIDANCE: Record<string, string> = {
  Essay:
    "Graded against the section rubric. Full marks need the mechanism, one clinical consequence, and a named example.",
  "Short Answer":
    "Accept any two correct items in either order. Spelling is not marked.",
}

/**
 * The windows live in `question-signals`, which reads them to say whether a
 * question played the way its tag claims. Generating from the same table is
 * what makes that reading true of the mock: a derived p-value drawn from some
 * other range would have every question drifting from its own tag.
 */
const P_VALUE_RANGE = TAGGED_DIFFICULTY_P_VALUE

function round(value: number, digits = 2): number {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}

/** ISO date `days` before a fixed today, so snapshots never drift. */
const TODAY = new Date("2026-07-01T00:00:00Z")

function isoDaysAgo(days: number): string {
  const date = new Date(TODAY)
  date.setUTCDate(date.getUTCDate() - days)
  return date.toISOString().slice(0, 10)
}

/** Long form for the metadata rows: `12/14/2025`. */
export function formatPreviewDate(iso: string): string {
  const [year, month, day] = iso.split("-")
  return `${month}/${day}/${year}`
}

export function showcaseItemRationale(stem: string): string {
  const text = stem.toLowerCase()
  if (
    text.includes("beta oxidation of fatty acids occurs in") ||
    text.includes("where does beta oxidation of fatty acids occur")
  ) {
    return "Beta oxidation of long-chain fatty acids occurs in the mitochondrial matrix."
  }
  if (text.includes("read the following passage") && text.includes("heartbeat")) {
    return "The sinoatrial node is the primary pacemaker in a healthy adult."
  }
  return ""
}

export const SHOWCASE_PACEMAKER_PASSAGE =
  "<p>The SA node pacemaker potential includes a slow inward current. That current is carried largely by sodium through funny channels.</p>"

export const SHOWCASE_PACEMAKER_PASSAGE_ALT =
  "<p>Phase 4 depolarization in SA node cells depends on If, an inward mixed cation current dominated by sodium.</p>"

export function showcaseStemHasPassage(stem: string): boolean {
  return /inward current of the cardiac pacemaker/i.test(stem)
}

/** No cohort has read this wording. Not zero: absent. */
const NEVER_SAT: QuestionPerformance = {
  pValue: null,
  pbi: null,
  dIndex: null,
  upper27: null,
  lower27: null,
  cohort: null,
}

function performance(
  row: AssessmentBankQuestion,
  seed: string,
  drift: number
): QuestionPerformance {
  const pbi = Number(row.pbi)
  if (Number.isNaN(pbi)) {
    return { ...NEVER_SAT }
  }

  const [low, high] = P_VALUE_RANGE[row.difficulty]
  const inWindow = low + hashUnit(`${seed}:p`) * (high - low)
  const d = discriminationIndex(row)

  // A minority of the bank is mistagged, because a real bank's is. Every
  // question landing inside its own window would make the panel's reading of
  // the two ("Harder than tagged") a control that never fires, and the whole
  // point of measuring difficulty is to catch the items whose tag is wrong.
  // Deterministic and small: about one in six, one band out, never enough to
  // make the tag look meaningless.
  const mistag = hashUnit(`${seed}:mistag`)
  const pValue = round(
    mistag < 0.09
      ? low - 0.06 - mistag
      : mistag > 0.91
        ? high + 0.06 + (1 - mistag)
        : inWindow
  )

  const satP = round(Math.min(0.97, Math.max(0.05, pValue - drift)))
  const satPbi = round(Math.max(0, pbi - drift))
  const satD = d === null ? null : round(Math.max(0, d - drift))
  // Upper/lower 27% correct rates: overall difficulty ± half the discrimination
  // gap, so a strong separator shows a wide split and a weak one almost none.
  const halfGap = (satD ?? satPbi) / 2
  const upper27 = round(Math.min(0.99, Math.max(0.01, satP + halfGap)))
  const lower27 = round(Math.min(0.99, Math.max(0.01, satP - halfGap)))

  return {
    // An earlier version was sat by an earlier cohort, so its numbers differ.
    // `drift` is that difference, not noise: it is what makes comparing
    // versions worth doing.
    pValue: satP,
    pbi: satPbi,
    dIndex: satD,
    upper27,
    lower27,
    cohort: 60 + Math.floor(hashUnit(`${seed}:cohort`) * 180),
  }
}

function terms(topic: string): string[] {
  return TOPIC_TERMS[topic] ?? FALLBACK_TERMS
}

/** A fill-in item needs a visible hole for each accepted answer. */
function stemWithAnswerHoles(stem: string, blankCount: number): string {
  const holes = (stem.match(/_{3,}/g) ?? []).length
  const markers = (stem.match(/\{\{blank\}\}/gi) ?? []).length
  if (holes + markers >= blankCount) return stem
  const missing = blankCount - holes - markers
  const extra = Array.from({ length: missing }, () => "________").join(" ")
  const trimmed = stem.replace(/[.?]\s*$/, "")
  return `${trimmed} ${extra}.`
}

/** Alternate accepted strings for a FIB blank (primary + abbreviation). */
function fillBlankAccepted(primary: string, seed: string): string[] {
  const accepted = [primary]
  const abbrev = primary
    .split(/\s+/)
    .filter((w) => /^[A-Z]/.test(w))
    .map((w) => w[0])
    .join("")
  if (abbrev.length >= 2 && abbrev !== primary && hashUnit(`${seed}:abbr`) > 0.35) {
    accepted.push(abbrev)
  }
  return accepted
}

/** Imported / library rows may carry a real key; prefer it over synthetic terms. */
function carriedFillBlankAccepted(row: AssessmentBankQuestion): string[] | null {
  const raw = row.carriedFillBlankAccepted
  if (!Array.isArray(raw)) return null
  const accepted = raw
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter(Boolean)
  return accepted.length > 0 ? accepted : null
}

function carriedChoiceOptions(
  row: AssessmentBankQuestion,
): { text: string; correct: boolean }[] | null {
  const raw = row.carriedChoices
  if (!Array.isArray(raw) || raw.length === 0) return null
  const choices: { text: string; correct: boolean }[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue
    const record = entry as { text?: unknown; isCorrect?: unknown; correct?: unknown }
    const text = typeof record.text === "string" ? record.text.trim() : ""
    if (!text) continue
    choices.push({
      text,
      correct: Boolean(record.isCorrect ?? record.correct),
    })
  }
  return choices.length > 0 ? choices : null
}

function carriedMatchingPairs(
  row: AssessmentBankQuestion,
): { prompt: string; match: string }[] | null {
  const raw = row.carriedMatchingPairs
  if (!Array.isArray(raw) || raw.length === 0) return null
  const pairs: { prompt: string; match: string }[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue
    const record = entry as { prompt?: unknown; match?: unknown }
    const prompt = typeof record.prompt === "string" ? record.prompt.trim() : ""
    const match = typeof record.match === "string" ? record.match.trim() : ""
    if (!prompt || !match) continue
    pairs.push({ prompt, match })
  }
  return pairs.length > 0 ? pairs : null
}

/** One spelling per type, from the shared model. */
function canonicalPoolType(type: string): string {
  return questionTypeLabel(type)
}

/**
 * The answer as students meet it.
 *
 * Shape follows the type and nothing else — `questionAnswerShape` decides,
 * so a question can never be handed an answer its type cannot hold. Anything
 * the type does not recognise falls through to `open`, which claims no key.
 */
function answerModel(
  row: AssessmentBankQuestion,
  seed: string,
  perf: QuestionPerformance
): QuestionAnswerModel {
  const type = canonicalPoolType(row.type)
  const shape = questionAnswerShape(row.type)
  if (shape === "fill_blank") {
    const authoredBlank = authoredFillBlankForStem(row.stem)
    if (authoredBlank) {
      return {
        kind: "fill_blank",
        blanks: [
          {
            id: "blank-1",
            label: "Blank",
            accepted: authoredBlank,
          },
        ],
      }
    }
    const carried = carriedFillBlankAccepted(row)
    if (carried) {
      const blankCount = Math.max(
        1,
        (row.stem.match(/_{3,}/g) ?? []).length +
          (row.stem.match(/\{\{blank\}\}/gi) ?? []).length,
      )
      const blanks =
        blankCount <= 1
          ? [
              {
                id: "blank-1",
                label: "Blank",
                accepted: carried,
              },
            ]
          : Array.from({ length: blankCount }, (_, index) => ({
              id: `blank-${index + 1}`,
              label: `Blank ${index + 1}`,
              accepted: [carried[index] ?? carried[0]!].filter(Boolean),
            }))
      return { kind: "fill_blank", blanks }
    }
    const pool = nonPositionalTerms(terms(row.topic), FILL_BLANK_FALLBACK)
    const offset = Math.floor(hashUnit(`${seed}:fib`) * pool.length)
    const blankCount = 1 + (hashUnit(`${seed}:fibn`) > 0.7 ? 1 : 0)
    return {
      kind: "fill_blank",
      blanks: Array.from({ length: blankCount }, (_, index) => {
        const primary = pool[(offset + index) % pool.length]
        return {
          id: `blank-${index + 1}`,
          label: blankCount === 1 ? "Blank" : `Blank ${index + 1}`,
          accepted: fillBlankAccepted(primary, `${seed}:b${index}`),
        }
      }),
    }
  }

  if (shape === "matching") {
    const carried = carriedMatchingPairs(row)
    if (carried) return { kind: "matching", pairs: carried }
    const pool = nonPositionalTerms(terms(row.topic), FILL_BLANK_FALLBACK)
    const offset = Math.floor(hashUnit(`${seed}:match`) * pool.length)
    const pairCount = 3
    // Pair each prompt with a later term so left and right columns differ.
    return {
      kind: "matching",
      pairs: Array.from({ length: pairCount }, (_, index) => {
        const prompt = pool[(offset + index) % pool.length] || `Prompt ${index + 1}`
        let match = pool[(offset + index + 2) % pool.length]
        if (!match || match === prompt) {
          match = pool[(offset + index + 1) % pool.length]
        }
        if (!match || match === prompt) match = `${prompt} finding`
        return { prompt, match }
      }),
    }
  }

  if (shape === "hotspot") {
    const { figure, regions } = hotspotKeyForStem(row.stem)
    return {
      kind: "hotspot",
      imageLabel: figure.imageLabel,
      imageSrc: figure.src,
      correctArea: { ...figure.correctArea },
      regions: regions.map((region) => ({ ...region })),
    }
  }

  const count = questionTypeChoiceCount(row.type)
  if (shape !== "choices" || !count) {
    return {
      kind: "open",
      guidance:
        OPEN_GUIDANCE[type] ??
        "Graded by hand against the section rubric.",
    }
  }

  const authoredChoices = authoredChoicesForStem(row.stem)
  const carriedChoices = carriedChoiceOptions(row)
  if (authoredChoices || carriedChoices) {
    const source = carriedChoices ?? authoredChoices!
    const multiple = questionTypeAllowsMultipleKeys(row.type)
    return {
      kind: "choices",
      multiple,
      choices: shareOut(
        source.map((choice, index) => ({
          letter: LETTERS[index] ?? String(index + 1),
          text: choice.text,
          correct: choice.correct,
        })),
        perf,
        seed
      ),
    }
  }

  if (normalizeQuestionType(row.type) === "true_false") {
    const trueIsCorrect = hashUnit(`${seed}:tf`) > 0.5
    return {
      kind: "choices",
      multiple: false,
      choices: shareOut(
        [
          { letter: "A", text: "True", correct: trueIsCorrect },
          { letter: "B", text: "False", correct: !trueIsCorrect },
        ],
        perf,
        seed
      ),
    }
  }

  const pool = terms(row.topic)
  const offset = Math.floor(hashUnit(`${seed}:offset`) * pool.length)
  const picked = Array.from(
    { length: count },
    (_, index) => pool[(offset + index) % pool.length]
  )
  const keyed = correctIndexForChoices(row.stem, picked)
  const keyIndex =
    keyed ?? Math.floor(hashUnit(`${seed}:key`) * count)
  const multiple = questionTypeAllowsMultipleKeys(row.type)
  // A Multiple Select item with one right answer is a Multiple Choice item
  // wearing the wrong label, so the second key is not optional.
  const secondKey = (keyIndex + 2) % count

  return {
    kind: "choices",
    multiple,
    choices: shareOut(
      picked.map((text, index) => ({
        letter: LETTERS[index],
        text: text.trim() || `Option ${LETTERS[index]}`,
        correct: index === keyIndex || (multiple && index === secondKey),
      })),
      perf,
      seed
    ),
  }
}

/**
 * Spreads the cohort across the choices so the shares add to 1 and the key
 * carries the p-value. Without that the numbers would contradict the KPI strip
 * two inches above them.
 */
function shareOut(
  choices: { letter: string; text: string; correct: boolean }[],
  perf: QuestionPerformance,
  seed: string
): QuestionChoice[] {
  const pValue = perf.pValue
  if (pValue === null) {
    return choices.map((choice) => ({ ...choice, share: null }))
  }

  const keys = choices.filter((choice) => choice.correct)
  const distractors = choices.filter((choice) => !choice.correct)
  const weights = distractors.map(
    (choice) => 0.15 + hashUnit(`${seed}:${choice.letter}`)
  )
  const weightSum = weights.reduce((total, weight) => total + weight, 0)
  const remainder = 1 - pValue

  return choices.map((choice) => {
    if (choice.correct) {
      return { ...choice, share: round(pValue / keys.length) }
    }
    const index = distractors.indexOf(choice)
    return { ...choice, share: round((remainder * weights[index]) / weightSum) }
  })
}

/** What each revision says it changed, in the order a question tends to evolve. */
const VERSION_NOTES = [
  "First draft",
  "Reworded the stem after student feedback",
  "Replaced the weakest distractor",
  "Tightened the key and rubric wording",
]

/**
 * How many revisions an item has. An unsat item is almost always a first draft;
 * one that has been through several cohorts has usually been edited between
 * them, and that history is the reason an author might want an older wording.
 */
function versionCount(row: AssessmentBankQuestion, seed: string): number {
  if (row.pbi === "--") return 1
  return 2 + Math.floor(hashUnit(`${seed}:versions`) * 2)
}

/**
 * Everything the preview needs for one row.
 *
 * Pure and cheap enough to call in a render, but the sheet memoizes it anyway
 * because switching versions re-renders and the shares would be recomputed for
 * a list that cannot have changed.
 */
export function questionPreview(row: AssessmentBankQuestion): QuestionPreview {
  const seed = row.id
  const authored = row.revisions ?? []
  const count = versionCount(row, seed)
  const versions: QuestionVersion[] = []

  for (let number = count; number >= 1; number -= 1) {
    const age = count - number
    // Older versions read as slightly less discriminating, which is the usual
    // direction of travel: the edits were made to fix that.
    const drift = age * 0.05
    const perf = performance(row, `${seed}:v${number}`, drift)
    const answer = answerModel(row, `${seed}:v${number}`, perf)
    const wording =
      number === count
        ? row.stem
        : `${row.stem.replace(/[.?]$/, "")} (version ${number} wording)`
    versions.push({
      number,
      note:
        number === 1
          ? VERSION_NOTES[0]
          : (VERSION_NOTES[number - 1] ?? "Revised"),
      author: row.author,
      authorEmail: row.authorEmail,
      date: isoDaysAgo(30 + age * 210 + Math.floor(hashUnit(`${seed}:d${number}`) * 45)),
      stem: answer.kind === "fill_blank" ? stemWithAnswerHoles(wording, answer.blanks.length) : wording,
      answer,
      performance: perf,
      // An authored revision takes the crown: the bank serves what the author
      // last saved, not what the hash last derived.
      current: number === count && authored.length === 0,
    })
  }

  // Newest first, so this session's revisions lead and the derived history
  // sits underneath them in the order it always had.
  const all = [
    ...authored
      .map((revision, index) => ({
        ...revision,
        number: count + index + 1,
        performance: NEVER_SAT,
        current: index === authored.length - 1,
      }))
      .reverse(),
    ...versions,
  ]

  return {
    versions: all,
    createdOn: all[all.length - 1].date,
    updatedOn: all[0].date,
  }
}
